import { type ContractInvoicingDto, datePl, EXT_STATUS_LABELS, formatMoney, INVOICE_KINDS, INVOICE_STATUSES, type InvoiceDto, KSEF_STATUS_LABELS } from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Badge, Button, Card, Checkbox, Group, Modal, NumberInput, Stack, Table, Text, Textarea, Tooltip } from '@mantine/core';
import { IconCash, IconFileInvoice, IconFileTypePdf, IconReceiptRefund, IconRefresh, IconSend } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { ask } from '../confirm';
import { formatDateTime, qtyPl } from '../format';
import { notifyError, notifyOk } from '../notify';
import { minHint } from '../minHint';
import { InvoicePaymentModal, PendingInvoicePayments } from './InvoicePayment';
import { InvoicePayStatusBadge, TestModeBadge } from './Badges';
import { Money, MoneyInput } from './Money';
import { QueryState } from './QueryState';
import { EmptyState, FilterChips } from './ui';
import { IssueWarnings } from './IssueWarnings';

/** D9: ostrzeżenia przy wystawianiu (np. firma bez numeru rachunku) — nie blokują. */
function BankWarnings({ st }: { st: ContractInvoicingDto }) {
  if (!st.warnings?.length) return null;
  return (
    <Alert color="yellow" variant="light" p="xs">
      <IssueWarnings items={st.warnings} />
    </Alert>
  );
}

const statusColor: Record<string, string> = { wystawiona: 'green', blad: 'red', niepewna: 'orange', wysylanie: 'blue' };

function InvoiceStatus({ i }: { i: InvoiceDto }) {
  if (i.status !== 'wystawiona')
    return (
      <Badge size="sm" variant="light" color={statusColor[i.status] ?? 'gray'}>
        {INVOICE_STATUSES[i.status]}
      </Badge>
    );
  // pro forma po wpłacie zamienia się w fakturę zaliczkową — nie jest już wezwaniem do zapłaty
  if (i.kind === 'proforma' && i.convertedTo.length)
    return (
      <Badge size="sm" variant="light" color={i.converted ? 'gray' : 'yellow'}>
        {i.converted ? 'Zamieniona na zaliczkową' : 'Częściowo opłacona'}
      </Badge>
    );
  return (
    <Stack gap={2}>
      {/* P2: saldo 0 (brutto − zaliczki − wpłaty) = opłacona, niezależnie od statusu w Fakturowni */}
      {i.kind !== 'zamowienie' && i.extStatus && (
        <InvoicePayStatusBadge extStatus={i.toPayGr === 0 ? 'paid' : i.extStatus} />
      )}
      {i.ksefStatus && i.ksefStatus !== 'not_applicable' && (
        <Text size="xs" c={i.ksefStatus.includes('error') ? 'red' : 'dimmed'}>
          {KSEF_STATUS_LABELS[i.ksefStatus] ?? `KSeF: ${i.ksefStatus}`}
          {i.ksefNumber ? ` · ${i.ksefNumber}` : ''}
        </Text>
      )}
    </Stack>
  );
}

/** Korekta: do zera albo nowe ilości / kwoty brutto pozycji. */
function CorrectionDialog({ inv, onClose, onDone }: { inv: InvoiceDto; onClose: () => void; onDone: (st: ContractInvoicingDto) => void }) {
  const [mode, setMode] = useState<'zero' | 'zmiana'>('zmiana');
  const [reason, setReason] = useState('');
  const [items, setItems] = useState(inv.items.map((it) => ({ position: it.position, quantity: it.quantity, grossGr: it.grossGr })));
  const save = useMutation({
    mutationFn: () => api<ContractInvoicingDto>(`/invoices/${inv.id}/correction`, { method: 'POST', body: { reason, mode, items: mode === 'zero' ? [] : items } }),
    onSuccess: (st) => {
      notifyOk('Wystawiono fakturę korygującą');
      onDone(st);
    },
    onError: notifyError,
  });
  const changed = items.some((x) => {
    const o = inv.items.find((it) => it.position === x.position)!;
    return o.quantity !== x.quantity || o.grossGr !== x.grossGr;
  });
  const newTotal = mode === 'zero' ? 0 : items.reduce((a, x) => a + x.grossGr, 0);
  return (
    <Modal opened onClose={onClose} title={`Korekta faktury ${inv.number ?? ''}`} size="lg">
      <Stack>
        <FilterChips
          value={mode}
          onChange={setMode}
          options={[
            { value: 'zmiana', label: 'Zmiana ilości / kwot' },
            { value: 'zero', label: 'Korekta do zera' },
          ]}
        />
        {mode === 'zero' && <Alert color="orange">Korekta do zera anuluje fakturę (np. błędny nabywca). Nową fakturę dla właściwego nabywcy wystawisz osobno.</Alert>}
        {mode === 'zmiana' && (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Pozycja</Table.Th>
                <Table.Th w={110}>Ilość</Table.Th>
                <Table.Th w={150}>Brutto</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {inv.items.map((it) => {
                const x = items.find((y) => y.position === it.position)!;
                const set = (patch: Partial<typeof x>) => setItems(items.map((y) => (y.position === it.position ? { ...y, ...patch } : y)));
                return (
                  <Table.Tr key={it.position}>
                    <Table.Td>
                      <Text size="sm">{it.name}</Text>
                      <Text size="xs" c="dimmed">
                        było: {qtyPl(it.quantity)} {it.unit} · {formatMoney(it.grossGr)} · VAT {it.vatRate}%
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <NumberInput size="xs" min={0} decimalScale={3} value={x.quantity} onChange={(v) => set({ quantity: Number(v) || 0 })} />
                    </Table.Td>
                    <Table.Td>
                      <MoneyInput size="xs" valueGr={x.grossGr} onChangeGr={(v) => set({ grossGr: Math.max(v ?? 0, 0) })} />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
        <Group justify="space-between">
          <Text size="sm">
            Brutto po korekcie: <b>{formatMoney(newTotal)}</b> (było {formatMoney(inv.grossGr ?? 0)}, różnica {formatMoney(newTotal - (inv.grossGr ?? 0))})
          </Text>
        </Group>
        <Textarea label="Przyczyna korekty" description={`${minHint(reason)}. Trafi na fakturę (i do KSeF) — do 256 znaków`} value={reason} onChange={(e) => setReason(e.currentTarget.value)} maxLength={256} autosize minRows={2} required />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button color="orange" loading={save.isPending} disabled={reason.trim().length < 3 || (mode === 'zmiana' && !changed)} onClick={() => save.mutate()}>
            Wystaw korektę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

const NEXT_LABEL = { zaliczkowa: 'Faktura zaliczkowa' } as const;

/** Do kogo trafi dokument po wystawieniu (wg ustawienia firmy). */
function deliveryLine(st: ContractInvoicingDto): string {
  if (!st.email.autoSend || st.email.mode === 'brak') return 'Nie zostanie wysłany automatycznie — wyślesz go przyciskiem „Wyślij” w zakładce „Faktury”.';
  if (!st.email.to) return 'Klient nie ma adresu e-mail w karcie — dokument nie zostanie wysłany.';
  return `Zostanie wysłany e-mailem do ${st.email.to} (${st.email.mode === 'crm' ? 'z CRM, PDF w załączniku' : 'z Fakturowni'}).`;
}

/** Jedno potwierdzenie dla wszystkich dokumentów: kwota, wysyłka, tryb konta / KSeF, nieodwracalność. */
export function confirmDocument(st: ContractInvoicingDto, title: string, lines: string[], fiscal = true) {
  return ask(
    <Stack gap={6}>
      {lines.map((l) => (
        <Text key={l} size="sm">
          {l}
        </Text>
      ))}
      <Text size="sm">{deliveryLine(st)}</Text>
      <BankWarnings st={st} />
      <Text size="sm" c="dimmed">
        {st.connection?.mode === 'test' ? 'Konto testowe Fakturowni.' : fiscal ? 'Konto produkcyjne — jeśli Fakturownia ma włączony KSeF, faktura trafi do KSeF.' : 'Konto produkcyjne.'}
      </Text>
      {fiscal && (
        <Alert color="orange" variant="light" p="xs">
          Wystawionej faktury nie da się usunąć — tylko skorygować.
        </Alert>
      )}
    </Stack>,
    { title, confirm: 'Wystaw' },
  );
}

/** Okno faktury końcowej (lub VAT bez zaliczek) — z harmonogramu, z zakładki Faktury i z Finansów (onIssued — odświeżenie listy). */
export function FinalInvoiceModal({ st, contractId, opened, onClose, onIssued }: { st: ContractInvoicingDto; contractId: string; opened: boolean; onClose: () => void; onIssued?: () => void }) {
  const qc = useQueryClient();
  // QA P4: domyślny termin z harmonogramu (najpóźniejszy termin rozliczanych pozycji), w ostateczności ustawienie firmy
  const suggested = st.finalDue?.days ?? 14;
  const [days, setDays] = useState(suggested);
  // ostrzeżenia (umowa niepodpisana, brak odbioru) — bez blokady, z potwierdzeniem (docs/09)
  const warnings = st.final.warnings ?? [];
  const [confirmed, setConfirmed] = useState(false);
  // QA P17: otwarte uwagi z odbioru — można wstrzymać automatyczny e-mail z fakturą (wysyłka ręczna po zamknięciu uwag)
  const remarksOpen = warnings.some((w) => w.startsWith('Odbiór z uwagami'));
  const canHold = remarksOpen && st.email.autoSend && st.email.mode !== 'brak';
  const [holdEmail, setHoldEmail] = useState(false);
  useEffect(() => {
    setConfirmed(false);
    setDays(suggested);
    setHoldEmail(false);
  }, [opened, suggested]);
  const scheduleDue = st.finalDue?.scheduleDue ?? null;
  const final = useMutation({
    mutationFn: () => api<ContractInvoicingDto>(`/contracts/${contractId}/invoices/final`, { method: 'POST', body: { paymentDays: days, ...(canHold && holdEmail ? { holdEmail: true } : {}) } }),
    onSuccess: (s) => {
      qc.setQueryData(['contract-invoicing', contractId], s);
      notifyOk(`Wystawiono ${st.final.kind === 'koncowa' ? 'fakturę końcową' : 'fakturę VAT'}`);
      onIssued?.();
      onClose();
    },
    onError: (e) => {
      notifyError(e);
      void qc.invalidateQueries({ queryKey: ['contract-invoicing', contractId] });
      onIssued?.();
    },
  });
  const toPay = st.final.contractGrossGr - st.final.advancesGr - st.final.alreadyPaidGr;
  return (
    <Modal opened={opened} onClose={onClose} title={st.final.kind === 'koncowa' ? 'Faktura końcowa' : 'Faktura VAT'}>
      <Stack>
        <Text size="sm">
          {st.final.kind === 'koncowa'
            ? `Wartość umowy ${formatMoney(st.final.contractGrossGr)}; rozliczy faktury zaliczkowe (${formatMoney(st.final.advancesGr)}).`
            : `Na całą wartość umowy: ${formatMoney(st.final.contractGrossGr)}.`}
          {st.final.alreadyPaidGr > 0 ? ` Wpłacono już ${formatMoney(st.final.alreadyPaidGr)} — faktura będzie oznaczona jako opłacona w tej części.` : ''}
        </Text>
        <Text size="sm" fw={600}>
          Do zapłaty: {formatMoney(Math.max(toPay, 0))}
        </Text>
        <NumberInput
          label="Termin płatności (dni)"
          description={scheduleDue ? `Z harmonogramu: termin ${datePl(scheduleDue)}${st.finalDue?.schedulePassed ? ' (minął — faktura z terminem na dziś)' : ''}` : st.finalDue ? 'Harmonogram nie podaje terminu — domyślnie wg ustawienia firmy' : undefined}
          min={0}
          max={120}
          value={days}
          onChange={(v) => setDays(Number(v) || 0)}
        />
        {scheduleDue && days > suggested && (
          <Alert color="yellow" variant="light" p="xs">
            Termin na fakturze będzie dłuższy niż w harmonogramie ({datePl(scheduleDue)}). Po wystawieniu obowiązuje termin z faktury — od niego liczą się „po terminie” i przypomnienia klientowi.
          </Alert>
        )}
        <Text size="xs" c="dimmed">
          Opłacenie oznaczone w Fakturowni samo pojawi się jako wpłata w CRM (sprawdzane co 15 minut albo przyciskiem „Odśwież”).
        </Text>
        <Text size="sm">{deliveryLine(st)}</Text>
        <BankWarnings st={st} />
        <Text size="xs" c="dimmed">
          {st.connection?.mode === 'test' ? 'Konto testowe Fakturowni.' : 'Konto produkcyjne — jeśli Fakturownia ma włączony KSeF, faktura trafi do KSeF.'}
        </Text>
        {warnings.length > 0 && (
          <Alert color="red" variant="light" title="Sprawdź przed wystawieniem">
            <Stack gap={6}>
              {warnings.map((w) => (
                <Text key={w} size="sm">
                  {w}
                </Text>
              ))}
              <Checkbox size="sm" label="Rozumiem — wystawiam mimo to (np. na życzenie klienta)" checked={confirmed} onChange={(e) => setConfirmed(e.currentTarget.checked)} />
              {canHold && (
                <Checkbox
                  size="sm"
                  label="Wstrzymaj e-mail z fakturą do zamknięcia uwag"
                  description="Faktura zostanie wystawiona, ale nie wysłana — wyślesz ją przyciskiem „Wyślij” w zakładce „Faktury”."
                  checked={holdEmail}
                  onChange={(e) => setHoldEmail(e.currentTarget.checked)}
                />
              )}
            </Stack>
          </Alert>
        )}
        <Alert color="orange" variant="light">
          Wystawionej faktury nie da się usunąć — tylko skorygować.
        </Alert>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button loading={final.isPending} disabled={warnings.length > 0 && !confirmed} onClick={() => final.mutate()}>
            Wystaw fakturę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * P1: faktura VAT do podpisanego aneksu — pozycje aneksu, rozlicza płatności aneksu (osobno od faktury końcowej umowy).
 * Ten sam adres co faktura końcowa, z identyfikatorem aneksu.
 */
export function AnnexInvoiceModal({ st, contractId, annexId, opened, onClose, onIssued }: { st: ContractInvoicingDto; contractId: string; annexId: string; opened: boolean; onClose: () => void; onIssued?: () => void }) {
  const qc = useQueryClient();
  const a = st.annexes?.find((x) => x.contractId === annexId);
  // QA6 P18: domyślny termin z harmonogramu aneksu (jak przy fakturze końcowej), w ostateczności ustawienie firmy
  const suggested = a?.due?.days ?? 14;
  const scheduleDue = a?.due?.scheduleDue ?? null;
  const [days, setDays] = useState(suggested);
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    setConfirmed(false);
    setDays(suggested);
  }, [opened, suggested]);
  const issue = useMutation({
    mutationFn: () => api<ContractInvoicingDto>(`/contracts/${annexId}/invoices/final`, { method: 'POST', body: { paymentDays: days } }),
    onSuccess: (s) => {
      qc.setQueryData(['contract-invoicing', contractId], s);
      notifyOk(`Wystawiono fakturę VAT do aneksu ${a?.number ?? ''}`.trim());
      onIssued?.();
      onClose();
    },
    onError: (e) => {
      notifyError(e);
      void qc.invalidateQueries({ queryKey: ['contract-invoicing', contractId] });
      onIssued?.();
    },
  });
  if (!a) return null;
  const warnings = a.warnings;
  return (
    <Modal opened={opened} onClose={onClose} title={`Faktura VAT do aneksu ${a.number ?? ''}`.trim()}>
      <Stack>
        <Text size="sm">
          Pozycje aneksu: {formatMoney(a.grossGr)} brutto.
          {a.scheduleGr !== a.grossGr ? ` Płatności aneksu w harmonogramie: ${formatMoney(a.scheduleGr)}.` : ''}
          {a.alreadyPaidGr > 0 ? ` Wpłacono już ${formatMoney(a.alreadyPaidGr)} — faktura będzie oznaczona jako opłacona w tej części.` : ''}
        </Text>
        <Text size="sm" fw={600}>
          Do zapłaty: {formatMoney(Math.max(a.grossGr - a.alreadyPaidGr, 0))}
        </Text>
        <NumberInput
          label="Termin płatności (dni)"
          description={scheduleDue ? `Z harmonogramu aneksu: termin ${datePl(scheduleDue)}${a.due?.schedulePassed ? ' (minął — faktura z terminem na dziś)' : ''}` : a.due ? 'Harmonogram aneksu nie podaje terminu — domyślnie wg ustawienia firmy' : undefined}
          min={0}
          max={120}
          value={days}
          onChange={(v) => setDays(Number(v) || 0)}
        />
        {scheduleDue && days > suggested && (
          <Alert color="yellow" variant="light" p="xs">
            Termin na fakturze będzie dłuższy niż w harmonogramie aneksu ({datePl(scheduleDue)}). Po wystawieniu obowiązuje termin z faktury.
          </Alert>
        )}
        <Text size="sm">{deliveryLine(st)}</Text>
        <BankWarnings st={st} />
        {warnings.length > 0 && (
          <Alert color="red" variant="light" title="Sprawdź przed wystawieniem">
            <Stack gap={6}>
              {warnings.map((w) => (
                <Text key={w} size="sm">
                  {w}
                </Text>
              ))}
              <Checkbox size="sm" label="Rozumiem — wystawiam mimo to" checked={confirmed} onChange={(e) => setConfirmed(e.currentTarget.checked)} />
            </Stack>
          </Alert>
        )}
        <Alert color="orange" variant="light">
          Wystawionej faktury nie da się usunąć — tylko skorygować.
        </Alert>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button loading={issue.isPending} disabled={warnings.length > 0 && !confirmed} onClick={() => issue.mutate()}>
            Wystaw fakturę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** P1: faktury VAT do podpisanych aneksów (karta w zakładce „Faktury”). */
function AnnexInvoicesCard({ st, contractId }: { st: ContractInvoicingDto; contractId: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const list = st.annexes ?? [];
  if (!list.length) return null;
  return (
    <Card withBorder padding="sm">
      <Text fw={700} size="sm">
        Faktury VAT do aneksów
      </Text>
      <Text size="xs" c="dimmed" mb="xs">
        Podpisany aneks z pozycjami fakturuje się osobno — faktura końcowa umowy go nie obejmuje. Wpłaty na płatności aneksu rejestruje się przy tej fakturze.
      </Text>
      <Stack gap={6}>
        {list.map((a) => (
          <Group key={a.contractId} justify="space-between" wrap="wrap">
            <Text size="sm">
              Aneks {a.number ?? ''} · <b>{formatMoney(a.grossGr)}</b>
              {a.invoice ? ` · ${a.invoice.number ?? INVOICE_STATUSES[a.invoice.status]}` : ''}
            </Text>
            {!a.invoice && st.can.issue && (
              <Tooltip label={a.reason} disabled={a.allowed} multiline w={260}>
                <Button size="compact-xs" variant="light" disabled={!a.allowed} leftSection={<IconFileInvoice size={13} />} onClick={() => setOpen(a.contractId)}>
                  Faktura VAT do aneksu
                </Button>
              </Tooltip>
            )}
          </Group>
        ))}
      </Stack>
      {open && <AnnexInvoiceModal st={st} contractId={contractId} annexId={open} opened onClose={() => setOpen(null)} />}
    </Card>
  );
}

/** Wystawienie faktury do wpłaty — rodzaj dobiera serwer (zaliczkowa / końcowa / VAT). */
function useIssueForPayment(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) => api<ContractInvoicingDto>(`/contracts/${contractId}/invoices/for-payment`, { method: 'POST', body: { paymentId, paymentDays: 0 } }),
    onSuccess: (st) => {
      qc.setQueryData(['contract-invoicing', contractId], st);
      notifyOk('Wystawiono fakturę');
    },
    onError: (e) => {
      notifyError(e);
      void qc.invalidateQueries({ queryKey: ['contract-invoicing', contractId] });
    },
  });
}

/** QA8 P5: pytanie przed wysyłką — przy dokumencie już wysłanym wyraźnie „ponownie” (adres, godzina, bez nowej należności). */
function sendQuestion(i: InvoiceDto): Parameters<typeof ask> {
  const doc = i.number ?? 'Dokument';
  if (i.emailStatus === 'wyslano')
    return [
      `${doc} wysłano już do klienta${i.emailTo ? ` na adres ${i.emailTo}` : ''}${i.emailSentAt ? ` (${formatDateTime(i.emailSentAt)})` : ''}. Klient dostanie ten sam dokument jeszcze raz, z tematem „(ponownie)” i dopiskiem, że to nie jest nowa należność.`,
      { title: 'Faktura już wysłana', confirm: 'Wyślij ponownie' },
    ];
  return [`${doc} trafi do klienta e-mailem${i.emailTo ? ` (${i.emailTo})` : ''}.`, { title: 'Wysłać fakturę?', confirm: 'Wyślij' }];
}

const DEADLINE_HINT = 'Termin wystawienia faktury do otrzymanej wpłaty: najpóźniej 15. dnia miesiąca po miesiącu wpłaty. To nie jest termin płatności — wpłata już jest.';

/** Faktura przy wpłacie: numer wystawionej albo przycisk z rodzajem i terminem (15. dzień następnego miesiąca). */
export function PaymentInvoiceCell({ contractId, paymentId, compact = false }: { contractId: string; paymentId: string; compact?: boolean }) {
  const q = useQuery({ queryKey: ['contract-invoicing', contractId], queryFn: () => api<ContractInvoicingDto>(`/contracts/${contractId}/invoicing`) });
  const issue = useIssueForPayment(contractId);
  const st = q.data;
  const p = st?.payments.find((x) => x.paymentId === paymentId);
  if (!st || !p) return null;
  if (p.invoice)
    return (
      <Group gap={6} wrap="nowrap">
        <IconFileInvoice size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
        <div>
          <Text size="xs" fw={600}>
            {p.invoice.number ?? INVOICE_KINDS[p.invoice.kind]}
          </Text>
          <Text size="xs" c={p.invoice.status === 'wystawiona' ? 'dimmed' : 'red'}>
            {INVOICE_KINDS[p.invoice.kind]}
            {p.invoice.status !== 'wystawiona' ? ` · ${INVOICE_STATUSES[p.invoice.status]} — zob. zakładkę „Faktury”` : ''}
          </Text>
        </div>
      </Group>
    );
  if (p.toFinal)
    return (
      <Text size="xs" c="dimmed">
        {p.annexNumber ? `rozliczy faktura VAT do aneksu ${p.annexNumber}` : 'rozliczy faktura końcowa'}
      </Text>
    );
  if (!st.connection || !p.next || !st.can.issue) return null;
  // QA8 P14: ustawowy termin wystawienia faktury do wpłaty (nie termin płatności)
  const deadlineText = (
    <Text size="xs" c={p.overdue ? 'red' : 'dimmed'}>
      {p.overdue ? `termin wystawienia minął (${datePl(p.deadline)})` : `wystaw do ${datePl(p.deadline)}`}
    </Text>
  );
  const deadline = p.blockReason ? (
    deadlineText
  ) : (
    <Tooltip label={DEADLINE_HINT} multiline w={280}>
      {deadlineText}
    </Tooltip>
  );
  if (p.blockReason)
    return (
      <Tooltip label={p.blockReason} multiline w={260}>
        <div>
          <Text size="xs" c="dimmed">
            {NEXT_LABEL[p.next]} — później
          </Text>
          {deadline}
        </div>
      </Tooltip>
    );
  const what = NEXT_LABEL[p.next];
  return (
    <Stack gap={2} align="flex-start">
      <Button
        size="compact-xs"
        style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        variant={p.overdue ? 'filled' : 'light'}
        color={p.overdue ? 'red' : 'eko'}
        leftSection={<IconFileInvoice size={13} />}
        loading={issue.isPending}
        onClick={async () => {
          if (await confirmDocument(st, 'Wystawić fakturę zaliczkową?', [`Faktura zaliczkowa na wpłatę ${formatMoney(p.amountGr)} z ${datePl(p.paidAt)} (${p.itemName}) — oznaczona jako opłacona.`])) issue.mutate(paymentId);
        }}
      >
        {compact ? 'Wystaw' : what}
      </Button>
      {deadline}
    </Stack>
  );
}

const EMAIL_LABEL: Record<string, { text: string; color: string }> = {
  wyslano: { text: 'wysłano', color: 'green' },
  blad: { text: 'błąd wysyłki', color: 'red' },
  brak_adresu: { text: 'brak e-maila klienta', color: 'orange' },
};

/** Dokument przy pozycji harmonogramu: pro forma (zaliczka / transza) albo faktura końcowa z terminem (płatność końcowa). */
export function ScheduleDocCell({ contractId, paymentItemId }: { contractId: string; paymentItemId: string }) {
  const qc = useQueryClient();
  const key = ['contract-invoicing', contractId];
  const q = useQuery({ queryKey: key, queryFn: () => api<ContractInvoicingDto>(`/contracts/${contractId}/invoicing`) });
  const [finalOpen, setFinalOpen] = useState(false);
  const done = (st: ContractInvoicingDto, msg: string) => {
    qc.setQueryData(key, st);
    notifyOk(msg);
  };
  const onErr = (e: unknown) => {
    notifyError(e);
    void qc.invalidateQueries({ queryKey: key });
  };
  const proforma = useMutation({ mutationFn: () => api<ContractInvoicingDto>(`/contracts/${contractId}/invoices/proforma`, { method: 'POST', body: { paymentItemId } }), onSuccess: (st) => done(st, 'Wystawiono pro formę'), onError: onErr });
  const st = q.data;
  const it = st?.schedule.find((x) => x.paymentItemId === paymentItemId);
  if (!st || !it || !st.connection) return null;

  // P1: płatność aneksu — rozlicza ją faktura VAT do aneksu (wystawienie w zakładce „Faktury”)
  if (it.annexContractId)
    return (
      <Text size="xs" c="dimmed">
        {it.coveredBy ? `w fakturze ${it.coveredBy.number ?? 'VAT'}` : `faktura VAT do aneksu ${it.annexNumber ?? ''} — zakładka „Faktury”`}
      </Text>
    );
  // P3: rata objęta wystawioną fakturą końcową — wpłata przy tej fakturze
  if (!it.isFinal && it.coveredBy && !it.proforma)
    return (
      <Text size="xs" c="dimmed">
        w fakturze {it.coveredBy.number ?? 'końcowej'}
      </Text>
    );

  if (it.isFinal) {
    const fin = st.invoices.find((i) => ['koncowa', 'vat'].includes(i.kind) && i.paymentItemId === paymentItemId);
    if (fin)
      return (
        <div>
          <Text size="xs" fw={600}>
            {fin.number ?? INVOICE_KINDS[fin.kind]}
          </Text>
          <Text size="xs" c="dimmed">
            {fin.paymentTo ? `termin ${datePl(fin.paymentTo)} · ` : ''}
            {fin.extStatus ? (EXT_STATUS_LABELS[fin.extStatus] ?? fin.extStatus) : INVOICE_STATUSES[fin.status]}
          </Text>
        </div>
      );
    if (!st.can.issue) return null;
    return (
      <>
        <Tooltip label={st.final.reason} disabled={st.final.allowed} multiline w={260}>
          <Button size="compact-xs" variant="light" disabled={!st.final.allowed} leftSection={<IconFileInvoice size={13} />} onClick={() => setFinalOpen(true)} style={{ whiteSpace: 'nowrap' }}>
            {st.final.kind === 'koncowa' ? 'Faktura końcowa' : 'Faktura VAT'}
          </Button>
        </Tooltip>
        <FinalInvoiceModal st={st} contractId={contractId} opened={finalOpen} onClose={() => setFinalOpen(false)} />
      </>
    );
  }
  if (it.proforma)
    return (
      <div>
        <Text size="xs" fw={600}>
          {it.proforma.number ?? 'Pro forma'}
        </Text>
        <Text size="xs" c={it.proforma.status !== 'wystawiona' ? 'red' : 'dimmed'}>
          pro forma
          {it.proforma.status !== 'wystawiona' ? ` · ${INVOICE_STATUSES[it.proforma.status]}` : ''}
          {it.proforma.emailStatus ? ` · ${EMAIL_LABEL[it.proforma.emailStatus]?.text ?? it.proforma.emailStatus}` : ''}
        </Text>
      </div>
    );
  if (!it.canProforma || !st.can.issue) return null;
  return (
    <Button
      size="compact-xs"
      variant="light"
      leftSection={<IconFileInvoice size={13} />}
      loading={proforma.isPending}
      style={{ whiteSpace: 'nowrap' }}
      onClick={async () =>
        (await confirmDocument(st, 'Wystawić pro formę?', [`Pro forma na ${formatMoney(it.amountGr - it.paidGr)} (${it.name}).`, 'To wezwanie do zapłaty — bez skutków podatkowych, nie trafia do KSeF.', ...(it.proformaWarning ? [it.proformaWarning] : [])], false)) && proforma.mutate()
      }
    >
      Pro forma
    </Button>
  );
}

/** Faktury umowy: zaliczkowe z wpłat, końcowa / VAT, korekty, PDF, stan opłacenia i KSeF. */
export function InvoicesPanel({ contractId, canConfigure }: { contractId: string; canConfigure: boolean }) {
  const qc = useQueryClient();
  const key = ['contract-invoicing', contractId];
  const q = useQuery({ queryKey: key, queryFn: () => api<ContractInvoicingDto>(`/contracts/${contractId}/invoicing`) });
  const [finalOpen, setFinalOpen] = useState(false);
  const [correcting, setCorrecting] = useState<InvoiceDto | null>(null);
  const [paying, setPaying] = useState<InvoiceDto | null>(null);
  const [showConverted, setShowConverted] = useState(false);
  const set = (st: ContractInvoicingDto) => {
    qc.setQueryData(key, st);
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const onErr = (e: unknown) => {
    notifyError(e);
    void qc.invalidateQueries({ queryKey: key });
  };
  const retry = useMutation({ mutationFn: (id: string) => api<ContractInvoicingDto>(`/invoices/${id}/retry`, { method: 'POST' }), onSuccess: (st) => (set(st), notifyOk('Ponowiono wystawienie faktury')), onError: onErr });
  const abandon = useMutation({ mutationFn: (id: string) => api<ContractInvoicingDto>(`/invoices/${id}/abandon`, { method: 'POST' }), onSuccess: (st) => (set(st), notifyOk('Porzucono próbę wystawienia')), onError: onErr });
  const sendDoc = useMutation({ mutationFn: (id: string) => api<ContractInvoicingDto>(`/invoices/${id}/send`, { method: 'POST' }), onSuccess: (st) => (set(st), notifyOk('Wysłano do klienta')), onError: onErr });
  const refresh = useMutation({ mutationFn: (id: string) => api<InvoiceDto>(`/invoices/${id}/refresh`, { method: 'POST' }), onSuccess: () => void qc.invalidateQueries({ queryKey: key }), onError: onErr });

  const st = q.data;
  if (!st)
    return (
      <QueryState isLoading={!q.error} error={q.error}>
        {null}
      </QueryState>
    );
  // pro formy zamienione na faktury zaliczkowe — schowane (dokument „przeszedł” w zaliczkową), do pokazania na żądanie
  const converted = st.invoices.filter((i) => i.kind === 'proforma' && i.converted);
  const docs = st.invoices.filter((i) => (i.kind !== 'zamowienie' || i.status !== 'wystawiona') && (showConverted || !(i.kind === 'proforma' && i.converted)));
  const estimate = st.invoices.find((i) => i.kind === 'zamowienie' && i.status === 'wystawiona');
  const awaiting = st.payments.filter((p) => !p.invoice && !p.toFinal);

  return (
    <Stack gap="md">
      {!st.connection ? (
        <Alert color="gray" icon={<IconFileInvoice />}>
          Firma tej umowy nie ma włączonej integracji z Fakturownią.{' '}
          {canConfigure ? (
            <>
              Skonfiguruj ją w{' '}
              <Anchor component={Link} to="/ustawienia/firmy">
                „Ustawienia → Firmy”
              </Anchor>
              .
            </>
          ) : (
            'Poproś właściciela o konfigurację.'
          )}
        </Alert>
      ) : (
        st.connection.mode === 'test' && (
          <Alert color="yellow" variant="light" py="xs">
            Konto testowe Fakturowni — faktury nie są prawdziwymi dokumentami księgowymi.
          </Alert>
        )
      )}

      {st.connection && st.can.issue && <BankWarnings st={st} />}

      {st.connection && awaiting.length > 0 && (
        <Card withBorder padding="sm">
          <Text fw={700} size="sm" mb={4}>
            Wpłaty bez faktury
          </Text>
          <Text size="xs" c="dimmed" mb="xs">
            Wpłata na zaliczkę lub transzę → faktura zaliczkowa (do 15. dnia następnego miesiąca). Wpłaty na płatność końcową rozlicza faktura końcowa.
          </Text>
          <Stack gap={8}>
            {awaiting.map((p) => (
                <Group key={p.paymentId} justify="space-between" wrap="wrap" align="flex-start">
                  <Text size="sm">
                    {datePl(p.paidAt)} · <b>{formatMoney(p.amountGr)}</b> · {p.itemName}
                  </Text>
                  <PaymentInvoiceCell contractId={contractId} paymentId={p.paymentId} />
                </Group>
              ))}
          </Stack>
        </Card>
      )}

      {st.connection && st.can.issue && (
        <Card withBorder padding="sm">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <div>
              <Text fw={700} size="sm">
                {st.final.kind === 'koncowa' ? 'Faktura końcowa' : 'Faktura VAT'} z terminem płatności
              </Text>
              <Text size="xs" c="dimmed">
                {st.final.kind === 'koncowa'
                  ? `Na koniec prac (do 30 dni od wykonania). Rozlicza zaliczki ${formatMoney(st.final.advancesGr)} z ${formatMoney(st.final.contractGrossGr)}; opłacenie z Fakturowni wróci jako wpłata.`
                  : `Na koniec prac (do 30 dni od wykonania) — ${formatMoney(st.final.contractGrossGr)}; opłacenie z Fakturowni wróci jako wpłata.`}
              </Text>
            </div>
            <Group gap="xs" align="flex-end">
              <Tooltip label={st.final.reason} disabled={st.final.allowed} multiline w={260}>
                <Button size="xs" color="eko" disabled={!st.final.allowed} onClick={() => setFinalOpen(true)}>
                  Wystaw fakturę
                </Button>
              </Tooltip>
            </Group>
          </Group>
          {!st.final.allowed && (
            <Text size="xs" c="dimmed" mt={4}>
              {st.final.reason}
            </Text>
          )}
        </Card>
      )}

      {st.connection && <AnnexInvoicesCard st={st} contractId={contractId} />}

      {docs.length === 0 ? (
        <Card padding={0}>
          <EmptyState compact title="Brak faktur do tej umowy" />
        </Card>
      ) : (
        <Table.ScrollContainer minWidth={620}>
          <Table verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Dokument</Table.Th>
                <Table.Th>Data</Table.Th>
                <Table.Th ta="right">Brutto</Table.Th>
                <Table.Th>Stan</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {docs.map((i) => (
                <Table.Tr key={i.id}>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {i.number ?? INVOICE_KINDS[i.kind]}
                      {i.mode === 'test' && <TestModeBadge ml={6} />}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {INVOICE_KINDS[i.kind]}
                      {i.annexNumber ? ` do aneksu ${i.annexNumber}` : ''}
                      {i.parentNumber && i.kind === 'korekta' ? ` do ${i.parentNumber}` : ''}
                      {i.settledByNumber ? ` · rozliczona w ${i.settledByNumber}` : ''}
                      {i.fromProformaNumber ? ` · z pro formy ${i.fromProformaNumber}` : ''}
                      {i.kind === 'proforma' && i.convertedTo.length ? ` · zamieniona na ${i.convertedTo.join(', ')}` : ''}
                      {i.correctionReason ? ` · ${i.correctionReason}` : ''}
                    </Text>
                    {i.emailStatus && EMAIL_LABEL[i.emailStatus] && (
                      <Text size="xs" c={EMAIL_LABEL[i.emailStatus]!.color}>
                        e-mail: {EMAIL_LABEL[i.emailStatus]!.text}
                        {i.emailTo ? ` (${i.emailTo})` : ''}
                      </Text>
                    )}
                    {i.lastError && i.status !== 'wystawiona' && (
                      <Text size="xs" c="red">
                        {i.lastError}
                      </Text>
                    )}
                    <PendingInvoicePayments contractId={contractId} pending={i.pendingPayments} canRetry={i.can.syncPayments} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{i.issueDate ? datePl(i.issueDate) : '—'}</Text>
                    {i.paymentTo && i.kind !== 'zaliczkowa' && (
                      <Text size="xs" c="dimmed">
                        termin {datePl(i.paymentTo)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">
                      <Money gr={i.grossGr} />
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <InvoiceStatus i={i} />
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap" justify="flex-end">
                      {i.can.pdf && (
                        <Tooltip label="PDF z Fakturowni">
                          <ActionIcon variant="subtle" color="gray" component="a" href={`/api/invoices/${i.id}/pdf`} target="_blank" rel="noopener" aria-label="PDF">
                            <IconFileTypePdf size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {i.can.send && (
                        <Tooltip label={i.emailStatus === 'wyslano' ? `Wysłano ${i.emailTo ?? ''} — wyślij ponownie` : 'Wyślij do klienta'}>
                          <ActionIcon variant="subtle" color={i.emailStatus === 'wyslano' ? 'green' : 'gray'} loading={sendDoc.isPending && sendDoc.variables === i.id} onClick={async () => (await ask(...sendQuestion(i))) && sendDoc.mutate(i.id)} aria-label="Wyślij">
                            <IconSend size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {i.can.refresh && (
                        <Tooltip label="Odśwież stan (opłacenie, KSeF)">
                          <ActionIcon variant="subtle" color="gray" loading={refresh.isPending && refresh.variables === i.id} onClick={() => refresh.mutate(i.id)} aria-label="Odśwież">
                            <IconRefresh size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {i.can.registerPayment && (
                        <Tooltip label={`Zarejestruj wpłatę${i.toPayGr !== null ? ` (pozostało ${formatMoney(i.toPayGr)})` : ''} — trafi też do Fakturowni`}>
                          <ActionIcon variant="subtle" color="green" onClick={() => setPaying(i)} aria-label="Zarejestruj wpłatę">
                            <IconCash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {i.can.correct && (
                        <Tooltip label="Faktura korygująca">
                          <ActionIcon variant="subtle" color="orange" onClick={() => setCorrecting(i)} aria-label="Korekta">
                            <IconReceiptRefund size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {i.can.retry && (
                        <Button size="compact-xs" variant="light" loading={retry.isPending && retry.variables === i.id} onClick={() => retry.mutate(i.id)}>
                          {i.status === 'niepewna' ? 'Sprawdź i ponów' : 'Ponów'}
                        </Button>
                      )}
                      {i.can.abandon && (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="gray"
                          onClick={async () => (await ask('Faktura nie powstała w Fakturowni — sprawdzimy to jeszcze raz przed porzuceniem.', { title: 'Porzucić próbę wystawienia?', confirm: 'Porzuć' })) && abandon.mutate(i.id)}
                        >
                          Porzuć
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {converted.length > 0 && (
        <Group gap={4}>
          <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setShowConverted((v) => !v)}>
            {showConverted ? 'Ukryj pro formy zamienione na faktury zaliczkowe' : `Pokaż pro formy zamienione na faktury zaliczkowe (${converted.length})`}
          </Button>
        </Group>
      )}
      {estimate && (
        <Text size="xs" c="dimmed">
          Podstawa zaliczek w Fakturowni: zamówienie {estimate.number} (nie jest fakturą, nie trafia do KSeF).
        </Text>
      )}
      <FinalInvoiceModal st={st} contractId={contractId} opened={finalOpen} onClose={() => setFinalOpen(false)} />
      {paying && <InvoicePaymentModal invoiceId={paying.id} number={paying.number} contractId={contractId} toPayGr={paying.toPayGr} opened onClose={() => setPaying(null)} />}
      {correcting && (
        <CorrectionDialog
          inv={correcting}
          onClose={() => setCorrecting(null)}
          onDone={(s) => {
            set(s);
            setCorrecting(null);
          }}
        />
      )}
    </Stack>
  );
}
